import { useState, useRef, useEffect } from 'react';
import { Chip, type ChipVariant } from '@/components/ui/Chip';
import type { ProvenanceRecord, ProvenanceSource } from '@arcanum-hq/shared-types';

interface ProvenanceChipProps {
  namedRange: string;
  value: string;
  /**
   * Provenance map for the active conversation, refined from
   * `agentState.inputProvenance` at the route boundary (Zod-parsed). `null`
   * means the schema-shape parse failed or `agentState` hasn't loaded yet —
   * defensive render with `agent` variant + empty popover.
   */
  inputProvenance: Record<string, ProvenanceRecord> | null;
}

/**
 * Inline provenance pill (directive §8.3, Phase 2 Session 5). Composes the
 * `<Chip>` primitive (Session 2) with click-to-scroll-Model-tab semantics
 * + hover popover with source metadata.
 *
 * Source-to-variant mapping (provenanceSourceSchema in shared-types):
 *   user           → user variant     (U glyph, accent-dim bg)
 *   market_data    → market variant   (M glyph, warn-dim bg)
 *   default        → system variant   (S glyph, panel bg)
 *   inferred / calculated / scenario → agent variant (A glyph, purple)
 *
 * Click dispatches `arcanum:scroll-to-cell` (existing prior art at
 * <ModelViewEditGroup> — Phase 1's listener handles it in the Model tab).
 */
function sourceToVariant(source: ProvenanceSource | undefined): ChipVariant {
  switch (source) {
    case 'user': return 'user';
    case 'market_data': return 'market';
    case 'default': return 'system';
    case 'inferred':
    case 'calculated':
    case 'scenario': return 'agent';
    default: return 'agent'; // unknown source — best-effort agent variant
  }
}

export function ProvenanceChip({ namedRange, value, inputProvenance }: ProvenanceChipProps) {
  const [hovered, setHovered] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);

  const record: ProvenanceRecord | undefined = inputProvenance?.[namedRange];
  const variant = sourceToVariant(record?.source);

  useEffect(() => {
    if (!hovered) return;
    // Defensive close on Escape — keeps popover from sticking after focus
    // moves. (Hover is the primary trigger; this is the keyboard escape.)
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setHovered(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [hovered]);

  const handleClick = () => {
    window.dispatchEvent(
      new CustomEvent('arcanum:scroll-to-cell', { detail: { namedRange } }),
    );
  };

  return (
    <span
      ref={containerRef}
      className="relative inline-flex"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid="provenance-chip-container"
    >
      <Chip
        variant={variant}
        label={namedRange}
        value={value}
        interactive
        onClick={handleClick}
      />
      {hovered && (
        <div
          role="tooltip"
          data-testid="provenance-chip-popover"
          className="absolute top-full left-0 mt-1 z-10 min-w-[200px] max-w-[300px] px-3 py-2 bg-panel-bg border border-panel-border rounded-[3px] shadow-md text-xs font-mono"
        >
          {record ? (
            <div className="flex flex-col gap-0.5">
              <div className="text-text-muted">
                Source: <span className="text-text-bright">{record.source}</span>
              </div>
              {typeof record.confidence === 'number' && (
                <div className="text-text-muted">
                  Confidence:{' '}
                  <span className="text-text-bright tabular-nums">
                    {(record.confidence * 100).toFixed(0)}%
                  </span>
                </div>
              )}
              {record.reasoning && (
                <div className="text-text-muted">
                  <span className="text-text-bright">{record.reasoning}</span>
                </div>
              )}
              {record.timestamp && (
                <div className="text-text-dim">{record.timestamp}</div>
              )}
            </div>
          ) : (
            <div className="text-text-dim">(no provenance recorded)</div>
          )}
        </div>
      )}
    </span>
  );
}
